import { useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { Show } from '@elfsight/lib-utils';
import { useSettings } from '@wu/widget-core';
import { useRect, useWindowOn } from 'react-use-rect';
import type { PropsWithClassName } from '../../../../../types';
import {
  getPlayerControls,
  getWindowHeight,
  useIsWaveformProgress
} from '../../../../helpers';
import { Buttons } from '../../../buttons';
import {
  Description,
  FREE_LINK_HEIGHT,
  Image,
  StyledFreeLink,
  TrackInfo
} from '../../../common';
import { useSource } from '../../../sources';
import { usePlayer } from '../../PlayerProvider';
import {
  FakeTrack,
  HoverTime,
  Loop,
  MinimizeButton,
  Next,
  PlaylistButton,
  PlaylistButtonComponent,
  PlayPause,
  Prev,
  ProgressBar,
  ProgressBarLoaded,
  ProgressBarPlayed,
  Shuffle,
  Time,
  Track,
  VolumeComponent,
  VolumeControlComponent,
  Waveform
} from '../../player-parts';
import { Playlist } from '../../playlist';
import { useFloating } from '../floating';

interface MobileProps extends PropsWithClassName {
  isFloating?: boolean;
}

export function Mobile({ isFloating = false, className }: MobileProps) {
  const [containerWidth, setContainerWidth] = useState(0);
  const { setHeight } = useFloating();
  const [containerRef] = useRect(
    ({ width, height }) => {
      setContainerWidth(width);
      setHeight?.(height);
    },
    {
      resize: true
    }
  );
  const { currentItem, items = [] } = useSource();
  const {
    displayFreeLink,
    playerParts,
    playlistHeight: settingsPlaylistHeight,
    sourceType,
    coverImage,
    closeButton,
    controls: derivedControls,
    existingPlayerControls,
    playerControls,
    minimizeButton
  } = useSettings();
  const { showPlayer } = usePlayer();

  const { image, title, description } = currentItem ?? {};
  const controls = getPlayerControls(
    derivedControls,
    existingPlayerControls,
    playerControls
  );

  const [playlistHeight, setPlaylistHeight] = useState(0);
  const [componentRef, revalidate] = useRect(({ height: componentHeight }) =>
    setPlaylistHeight(getWindowHeight() - componentHeight - 24)
  );

  useWindowOn('resize', () => revalidate({ force: true }));

  const calculatedMaxHeight = useMemo(() => {
    const playlistHeightString = settingsPlaylistHeight
      .toString()
      .toLowerCase();
    const playlistHeightNumber = parseInt(playlistHeightString, 10);

    const isAutoHeight = ['100%', 'auto'].includes(playlistHeightString);

    if (isAutoHeight) {
      return playlistHeight;
    }

    if (isFloating) {
      return Math.min(playlistHeightNumber, playlistHeight);
    }

    return playlistHeightNumber;
  }, [settingsPlaylistHeight, playlistHeight, isFloating]);

  const paddingBottom = displayFreeLink ? FREE_LINK_HEIGHT + 10 : null;

  const withPlaylist = controls.includes('playlist');

  const withVolume = controls.includes('volume');
  const withTrackControls = controls.includes('trackControls');

  const minimizeOrCloseBtn = closeButton || minimizeButton;
  const withInfoPanel =
    playerParts.includes('image') ||
    playerParts.includes('title') ||
    playerParts.includes('author');

  const withChangeTrackControls = sourceType === 'rss' || items?.length > 1;

  const isAudioPlayerWithItems = sourceType === 'player' && items?.length > 1;
  const isWaveformProgress = useIsWaveformProgress();

  const isShowControlsMain =
    withTrackControls ||
    (!!items?.length &&
      !isAudioPlayerWithItems &&
      (withVolume || withPlaylist));

  const isShowControlsSecond =
    isAudioPlayerWithItems &&
    (controls.includes('shuffle') ||
      controls.includes('loop') ||
      controls.includes('volume') ||
      withPlaylist);

  const { url } = coverImage ?? {};

  return (
    <Container
      ref={containerRef}
      className={className}
      _withBoxShadow={isFloating}
      _withTopPadding={minimizeOrCloseBtn && !withInfoPanel}
      _paddingBottom={paddingBottom}
    >
      <Component ref={componentRef}>
        {controls.includes('progressBar') && (
          <Show when={isWaveformProgress} fallback={<StyledProgressBar />}>
            <Waveform />
          </Show>
        )}

        {(withInfoPanel || minimizeButton) && (
          <InfoContainer>
            {playerParts.includes('image') && (
              <ImageContainer
                trackName={title}
                link={url || image}
                _size={48}
              />
            )}

            <StyledTrackInfo _withPadding={minimizeOrCloseBtn} />

            {minimizeButton && (
              <StyledMinimizeContainer>
                <StyledMinimizeButton />
              </StyledMinimizeContainer>
            )}
          </InfoContainer>
        )}

        {playerParts.includes('description') && !!description && (
          <Description text={description} />
        )}

        {(isShowControlsMain || isShowControlsSecond) && (
          <Controls>
            {isShowControlsMain && (
              <ControlsMain>
                {withTrackControls && (
                  <>
                    {withChangeTrackControls && <Prev />}
                    <PlayPause />
                    {withChangeTrackControls && <Next />}
                  </>
                )}

                {!!items?.length && !isAudioPlayerWithItems && (
                  <>
                    {withVolume && <VolumeComponent />}
                    {withPlaylist && <PlaylistButton />}
                  </>
                )}
              </ControlsMain>
            )}

            {isShowControlsSecond && (
              <ControlsSecond>
                {controls.includes('shuffle') && <Shuffle />}
                {controls.includes('loop') && <Loop />}

                {withVolume && <StyledVolumeComponent />}
                {withPlaylist && <PlaylistButton />}
              </ControlsSecond>
            )}
          </Controls>
        )}

        {!!currentItem?.buttons?.length && (
          <ButtonWrapper>
            <Buttons containerWidth={containerWidth} />
          </ButtonWrapper>
        )}
      </Component>

      <StyledPlaylist maxHeight={calculatedMaxHeight} hideDirection="toTop" />
      {showPlayer && <StyledFreeLink isMobileView />}
    </Container>
  );
}

const StyledVolumeComponent = styled(VolumeComponent)`
  width: 24px;
  margin-left: 16px;
`;

const Container = styled.div<{
  _withBoxShadow: boolean;
  _withTopPadding?: boolean;
  _paddingBottom: number | null;
}>`
  ${({ _withBoxShadow, _withTopPadding, _paddingBottom }) => css`
    width: 100%;
    position: relative;

    ${_withBoxShadow &&
    css`
      box-shadow: 0 2px 15px rgba(0, 0, 0, 0.15);
    `}
    ${_withTopPadding &&
    css`
      padding-top: 44px;
    `}
    ${_paddingBottom &&
    css`
      padding-bottom: ${_paddingBottom ?? 0}px;
    `}
  `}
`;

const Component = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  width: 100%;
  padding: 5px 12px 12px;
  box-sizing: border-box;
`;

const StyledProgressBar = styled(ProgressBar)`
  width: 100%;
  height: 26px;
  top: -16px;
  left: 0;
  display: flex;
  align-items: flex-end;
  position: absolute;
  background-color: transparent;
  z-index: 1;

  & ${Track} {
    display: flex;
    align-items: flex-end;
    border-radius: 0;
  }

  & ${Time} {
    display: none;
  }

  & ${FakeTrack}, & ${ProgressBarPlayed}, & ${ProgressBarLoaded} {
    border-radius: 0;
  }

  & ${HoverTime} {
    margin-top: 0;
    transform: translate(-50%, -50%);
  }
`;

const StyledPlaylist = styled(Playlist)`
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  margin-top: -6px;
  padding: 0 8px;
  opacity: 1;
  transition-timing-function: ease-out;
`;

const InfoContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  z-index: 1;
  padding: 6px 0;
`;

const StyledTrackInfo = styled(TrackInfo)<{
  _withPadding?: boolean;
}>`
  ${({ _withPadding }) => _withPadding && 'padding-right: 20px;'}
`;

const ImageContainer = styled(Image)<{
  _size: number;
}>`
  position: relative;
  flex-shrink: 0;
  border-radius: 6px;
  overflow: hidden;
  margin-right: 12px;
  width: ${({ _size }) => _size}px;
  height: ${({ _size }) => _size}px;
`;

const Controls = styled.div`
  display: flex;
  width: 100%;
  flex-shrink: 0;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-top: 6px;
  z-index: 1;
`;

const ControlsMain = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  margin: 12px 0;

  & ${VolumeControlComponent} {
    margin-left: auto;
  }

  & ${PlaylistButtonComponent} {
    margin-left: 16px;
  }
`;

const ControlsSecond = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: right;
  margin: 18px 0;
`;

const ButtonWrapper = styled.div`
  width: 100%;
  padding-top: 10px;
`;

const StyledMinimizeContainer = styled.div`
  flex-grow: 1;
  display: flex;
  justify-content: flex-end;
`;

const StyledMinimizeButton = styled(MinimizeButton)`
  margin: 0;
  width: 32px;
  flex-basis: 32px;
  height: 32px;
`;
